/**
 * Integrytas Group - Document Management System (DMS)
 * Service: DmsService  (BASE SERVICE)
 *
 * The single, application-agnostic entry point for all document file handling
 * and versioning. Any module that needs document attachments calls these
 * functions with a polymorphic target (entityName + recordId) and never has to
 * know about storage buckets or the version chain.
 *
 * Storage interactions go through the csm-api gateway (signed upload + signed
 * download against the private `dms-documents` bucket); versioning and the
 * audit chain are owned by the dms_* SECURITY DEFINER RPCs. Apps inherit this
 * behaviour as-is, or compose around it.
 */

import { apiDb } from '../../lib/api_client';
import { executeCommonRoutine } from '../../lib/common_routine';
import { validateDocumentFile, buildStoragePath } from './DmsCommonRoutine';

export const DMS_BUCKET = 'dms-documents';
export const DMS_ENTITY = 'dms_document';

export interface DmsDocument {
  document_id: number;
  root_document_id: number;
  parent_document_id: number | null;
  version_number: number;
  is_latest: 'Y' | 'N';
  target_entity_name: string;
  target_record_id: string;
  original_name: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  storage_bucket: string;
  storage_path: string;
  uploaded_by_user_id: number | null;
  upload_timestamp: string;
  is_active: 'Y' | 'N';
  title: string | null;
  description: string | null;
}

/** Optional human title + description supplied at upload time. */
export interface DocMeta {
  title?: string | null;
  description?: string | null;
}

export interface DmsNote {
  note_id: number;
  entity_name: string;
  record_id: string;
  parent_note_id: number | null;
  note_text: string;
  created_by_user_id: number | null;
  is_active: 'Y' | 'N';
  created_at: string;
}

export interface UploadTarget {
  targetEntityName: string;
  targetRecordId: string;
  actorUserId?: number | null;
}

interface RpcResult { ok: boolean; error_code?: string; document?: DmsDocument }

function rpcError(code: string): Error {
  return new Error(code);
}

// ---------------------------------------------------------------------------
// File storage (private bucket via the gateway)
// ---------------------------------------------------------------------------

/** Upload bytes to the DMS bucket and return the stored object path. */
async function putBytes(target: UploadTarget, file: File): Promise<string> {
  const path = buildStoragePath(target.targetEntityName, target.targetRecordId, file.name);
  const { error } = await apiDb.storage.from(DMS_BUCKET).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (error) throw new Error(error.message);
  return path;
}

/**
 * Rebase a signed URL onto the origin the BROWSER can reach.
 *
 * Storage mints the URL from the SUPABASE_URL the gateway sees, which inside
 * Docker is the container-internal `http://kong:8000` — a host no browser can
 * resolve, so the link silently loads nothing. The token and path are what
 * matter; the origin is ours to choose. In a deployed environment the two
 * origins already agree and this is a no-op.
 */
function onCallerOrigin(signedUrl: string): string {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/+$/, '');
  if (!base) return signedUrl;
  const marker = signedUrl.indexOf('/storage/v1/');
  return marker === -1 ? signedUrl : base + signedUrl.slice(marker);
}

/** Time-limited signed URL for viewing/downloading a stored document. */
export async function getSignedUrl(storagePath: string, expiresIn = 300): Promise<string> {
  const { data, error } = await apiDb.storage.from(DMS_BUCKET).createSignedUrl(storagePath, expiresIn);
  if (error) throw new Error(error.message);
  const url = (data as { signedUrl?: string } | null)?.signedUrl;
  if (!url) throw new Error('DMS.STORAGE.SIGN_FAILED');
  return onCallerOrigin(url);
}

// ---------------------------------------------------------------------------
// Versioned documents (base-service RPCs)
// ---------------------------------------------------------------------------

/** Upload a brand-new document (version 1) for the given target record. */
export async function uploadNewDocument(target: UploadTarget, file: File, meta?: DocMeta): Promise<DmsDocument> {
  const v = validateDocumentFile(file);
  if (!v.valid) throw new Error(v.errors[0]?.message ?? 'DMS.FILE.INVALID');

  const storagePath = await putBytes(target, file);
  const { data, error } = await apiDb.rpc('dms_register_document', {
    p_payload: {
      target_entity_name: target.targetEntityName,
      target_record_id:   target.targetRecordId,
      original_name:      file.name,
      file_name:          storagePath.split('/').pop(),
      file_type:          file.type || null,
      file_size:          file.size,
      storage_bucket:     DMS_BUCKET,
      storage_path:       storagePath,
      title:              meta?.title ?? null,
      description:        meta?.description ?? null,
      uploaded_by_user_id: target.actorUserId ?? null,
    },
  });
  if (error) throw new Error(error.message);
  const res = data as RpcResult;
  if (!res?.ok || !res.document) throw rpcError(res?.error_code ?? 'DMS.REGISTER.FAILED');
  return res.document;
}

/** Upload an explicit NEW VERSION against an existing document family. */
export async function uploadNewVersion(
  documentId: number,
  file: File,
  actorUserId?: number | null,
  meta?: DocMeta,
): Promise<DmsDocument> {
  const v = validateDocumentFile(file);
  if (!v.valid) throw new Error(v.errors[0]?.message ?? 'DMS.FILE.INVALID');

  // Path is scoped by the document family so versions live together.
  const path = buildStoragePath(DMS_ENTITY, String(documentId), file.name);
  const { error: upErr } = await apiDb.storage.from(DMS_BUCKET).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (upErr) throw new Error(upErr.message);

  const { data, error } = await apiDb.rpc('dms_add_version', {
    p_document_id: documentId,
    p_payload: {
      file_name:      path.split('/').pop(),
      file_type:      file.type || null,
      file_size:      file.size,
      storage_bucket: DMS_BUCKET,
      storage_path:   path,
      title:          meta?.title ?? null,
      description:    meta?.description ?? null,
      uploaded_by_user_id: actorUserId ?? null,
    },
  });
  if (error) throw new Error(error.message);
  const res = data as RpcResult;
  if (!res?.ok || !res.document) throw rpcError(res?.error_code ?? 'DMS.VERSION.FAILED');
  return res.document;
}

/** Full version history (newest first) for the family of the given document. */
export async function getVersions(documentId: number): Promise<DmsDocument[]> {
  const { data, error } = await apiDb.rpc('dms_get_versions', { p_document_id: documentId });
  if (error) throw new Error(error.message);
  return (data ?? []) as DmsDocument[];
}

/** Revert: make a new latest version that re-points at a prior version's bytes. */
export async function revertToVersion(documentId: number): Promise<DmsDocument> {
  const { data, error } = await apiDb.rpc('dms_revert_version', { p_document_id: documentId });
  if (error) throw new Error(error.message);
  const res = data as RpcResult;
  if (!res?.ok || !res.document) throw rpcError(res?.error_code ?? 'DMS.REVERT.FAILED');
  return res.document;
}

/** Latest version of every distinct document attached to a target record. */
export async function listLatestForTarget(
  targetEntityName: string,
  targetRecordId: string,
): Promise<DmsDocument[]> {
  const { data, error } = await apiDb
    .from('dms_document')
    .select('*')
    .eq('target_entity_name', targetEntityName)
    .eq('target_record_id', targetRecordId)
    .eq('is_latest', 'Y')
    .eq('is_active', 'Y')
    .order('upload_timestamp', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as DmsDocument[];
}

/** Most recent documents uploaded by a given user, across every target record. */
export async function listRecentForUser(userId: number, limit = 30): Promise<DmsDocument[]> {
  const { data, error } = await apiDb
    .from('dms_document')
    .select('*')
    .eq('uploaded_by_user_id', userId)
    .eq('is_latest', 'Y')
    .eq('is_active', 'Y')
    .order('upload_timestamp', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as DmsDocument[];
}

// ---------------------------------------------------------------------------
// Metadata (tags / categories / retention) - funnelled through Common Routine
// ---------------------------------------------------------------------------

export async function setMetadata(
  rootDocumentId: number,
  metaKey: string,
  metaValue: string,
  actorUserId?: number | null,
): Promise<void> {
  const res = await executeCommonRoutine({
    table_name: 'dms_document_metadata',
    operation:  'ADD',
    row: { document_id: rootDocumentId, meta_key: metaKey, meta_value: metaValue },
    actor_user_id: actorUserId ?? null,
  });
  if (res.result_code !== 'OK') throw new Error(res.errors?.[0]?.message ?? 'DMS.META.FAILED');
}

export async function getMetadata(rootDocumentId: number): Promise<{ meta_key: string; meta_value: string | null }[]> {
  const { data, error } = await apiDb
    .from('dms_document_metadata')
    .select('meta_key, meta_value')
    .eq('document_id', rootDocumentId)
    .order('meta_key');
  if (error) throw new Error(error.message);
  return (data ?? []) as { meta_key: string; meta_value: string | null }[];
}

// ---------------------------------------------------------------------------
// Polymorphic notes / comment threads
// ---------------------------------------------------------------------------

export async function addNote(
  documentId: number,
  noteText: string,
  parentNoteId?: number | null,
  actorUserId?: number | null,
): Promise<void> {
  const res = await executeCommonRoutine({
    table_name: 'dms_note',
    operation:  'ADD',
    row: {
      entity_name:    DMS_ENTITY,
      record_id:      String(documentId),
      parent_note_id: parentNoteId ?? null,
      note_text:      noteText,
      created_by_user_id: actorUserId ?? null,
    },
    actor_user_id: actorUserId ?? null,
  });
  if (res.result_code !== 'OK') throw new Error(res.errors?.[0]?.message ?? 'DMS.NOTE.FAILED');
}

export async function getNotes(documentId: number): Promise<DmsNote[]> {
  const { data, error } = await apiDb
    .from('dms_note')
    .select('*')
    .eq('entity_name', DMS_ENTITY)
    .eq('record_id', String(documentId))
    .eq('is_active', 'Y')
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as DmsNote[];
}

export const DmsService = {
  DMS_BUCKET, DMS_ENTITY,
  uploadNewDocument, uploadNewVersion, getVersions, revertToVersion,
  listLatestForTarget, listRecentForUser, getSignedUrl,
  setMetadata, getMetadata, addNote, getNotes,
};
